import {
  clamp01,
  lerp,
  lerpHex,
  rainDropStrokeStyle,
  smoothstep,
} from '../math';
import { bakeGlint } from '../sprites';

interface RainDrop {
  x: number;
  y: number;
  speed: number;
  length: number;
}

interface Ring {
  x: number;
  life: number;
}

/** Rain rings live this long, seconds. */
const RING_LIFE_S = 0.35;

/** At most this many rings on the water at once. */
const RING_MAX = 10;

export interface WaterFrame {
  width: number;
  height: number;
  baseY: number;
  daylight: number;
  /** Eased rain intensity, 0 to 1. */
  rain: number;
  /** Eased cloud cover, 0 to 1. Dims the glint. */
  cloud: number;
  /** Wind ramp, 0 to 1. Leans the rain. */
  windRamp: number;
  /** The scrubbed hour, 0 to 23. Moves the sun glint east to west. */
  hour: number;
  /** Wave draw cap, px. */
  clampPx: number;
  dt: number;
  reducedMotion: boolean;
}

/**
 * The water: the surface polyline, the fill beneath it, the waterline
 * highlight, the sun/moon glint, and the rain that falls into it.
 *
 * The surface polyline is the shared reference for everything that has to sit on
 * the water — foam, rain rings, hull churn — so it is built once per frame and
 * handed out via `surfaceYAt`.
 */
export class WaterRenderer {
  private gradient: CanvasGradient | null = null;

  private gradientKey = -1;

  private glintSprite: HTMLCanvasElement | null = null;

  private readonly rainDrops: RainDrop[] = [];

  private readonly rings: Ring[] = [];

  /** The surface polyline for this frame, as `[x, y]` pairs. */
  private points: Array<[number, number]> = [];

  private stepX = 2;

  constructor(private readonly ctx: CanvasRenderingContext2D) {}

  invalidate(): void {
    this.gradientKey = -1;
  }

  /**
   * Sample the surface across the canvas. Called before anything that needs to
   * know where the water is — including the rain, so drops can end at the actual
   * surface rather than a nominal horizon.
   */
  buildSurface(
    width: number,
    baseY: number,
    clampPx: number,
    stepX: number,
    elevationAt: (x: number) => number,
  ): void {
    this.stepX = stepX;
    this.points = [];

    for (let x = 0; x <= width; x += stepX) {
      const y = baseY - Math.max(-clampPx, Math.min(clampPx, elevationAt(x)));
      this.points.push([x, y]);
    }
  }

  /** A free lookup into the surface polyline. */
  surfaceYAt = (x: number): number => {
    const index = Math.max(
      0,
      Math.min(this.points.length - 1, Math.round(x / this.stepX)),
    );

    return this.points[index][1];
  };

  surfacePoints(): ReadonlyArray<[number, number]> {
    return this.points;
  }

  /**
   * Soft drizzle, drawn *behind* the water so it vanishes at the sea surface and
   * leaves the boat crisp. The lean follows the wind (matching the sky streaks
   * and the foam drift, all blown left), and each drop that reaches the surface
   * seeds a small ripple ring drawn later on the water.
   */
  drawRain({
    width,
    height,
    daylight,
    rain,
    windRamp,
    dt,
    reducedMotion,
  }: WaterFrame): void {
    if (rain <= 0.02 || reducedMotion) {
      this.rainDrops.length = 0;

      return;
    }

    const { ctx } = this;
    // A few more drops by day so the darker streaks still read as rain.
    const want = Math.round(rain * (55 + 20 * daylight));

    while (this.rainDrops.length < want) {
      this.rainDrops.push({
        x: Math.random() * width,
        y: Math.random() * height,
        speed: 300 + Math.random() * 170,
        length: 6 + Math.random() * 6,
      });
    }

    const slant = 0.06 + 0.5 * windRamp;

    ctx.strokeStyle = rainDropStrokeStyle(daylight, rain);
    ctx.lineWidth = 0.85 + 0.35 * daylight;
    ctx.beginPath();

    this.rainDrops.forEach((drop) => {
      /* eslint-disable no-param-reassign */
      drop.y += drop.speed * dt;
      drop.x -= drop.speed * slant * dt;

      if (drop.x < -12) {
        drop.x += width + 24;
      }

      if (drop.y >= this.surfaceYAt(drop.x)) {
        if (this.rings.length < RING_MAX && Math.random() < 0.35) {
          this.rings.push({ x: drop.x, life: RING_LIFE_S });
        }

        drop.y = -10;
        drop.x = Math.random() * width;
      }
      /* eslint-enable no-param-reassign */

      ctx.moveTo(drop.x, drop.y);
      ctx.lineTo(drop.x - drop.length * slant, drop.y + drop.length);
    });

    ctx.stroke();

    if (this.rainDrops.length > want + 24) {
      this.rainDrops.length = Math.max(want, 0);
    }
  }

  /**
   * Fill everything below the surface line, covering the submerged hull. Built
   * as a `Path2D` so the fill and the glint clip share the same polygon.
   */
  drawWater(frame: WaterFrame): Path2D {
    const { ctx } = this;
    const { width, height, baseY, daylight } = frame;

    const path = new Path2D();
    path.moveTo(0, height);
    this.points.forEach(([x, y]) => path.lineTo(x, y));
    path.lineTo(width, height);
    path.closePath();

    // The water gradient only varies with daylight, so it caches on that alone.
    const key = Math.round(daylight * 48);

    if (key !== this.gradientKey) {
      this.gradient = ctx.createLinearGradient(
        0,
        baseY - height * 0.3,
        0,
        height,
      );
      this.gradient.addColorStop(0, lerpHex('102538', '6CCDF2', daylight));
      this.gradient.addColorStop(0.5, lerpHex('0c1d2c', '2C9BD6', daylight));
      this.gradient.addColorStop(1, lerpHex('07141f', '15709F', daylight));
      this.gradientKey = key;
    }

    if (this.gradient != null) {
      ctx.fillStyle = this.gradient;
      ctx.fill(path);
    }

    // waterline highlight
    ctx.beginPath();
    this.points.forEach(([x, y], i) => {
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.strokeStyle = `rgba(255,255,255,${0.05 + 0.5 * daylight})`;
    ctx.lineWidth = 1.4;
    ctx.stroke();

    this.drawGlint(frame, path);

    return path;
  }

  /**
   * A soft light column on the water. It tracks the hour east-to-west by day and
   * sits as a faint fixed moon path at night, dimmed by cloud. One clipped
   * `drawImage` per frame.
   */
  private drawGlint(
    { width, height, baseY, daylight, cloud, hour, clampPx }: WaterFrame,
    waterPath: Path2D,
  ): void {
    const sunAlpha = 0.2 * daylight * (1 - 0.8 * cloud);
    const moonAlpha = 0.1 * (1 - daylight) * (1 - 0.9 * cloud);
    const alpha = sunAlpha + moonAlpha;

    if (alpha <= 0.015) {
      return;
    }

    if (this.glintSprite == null) {
      this.glintSprite = bakeGlint();
    }

    const { ctx } = this;
    const dayProgress = clamp01((hour - 5) / 15);
    const x = lerp(
      width * 0.62,
      width * (0.85 - 0.7 * dayProgress),
      smoothstep(0.05, 0.5, daylight),
    );
    const glintWidth = width * 0.3;
    const top = Math.max(0, baseY - clampPx);

    ctx.save();
    ctx.clip(waterPath);
    ctx.globalAlpha = alpha;
    ctx.drawImage(
      this.glintSprite,
      x - glintWidth / 2,
      top,
      glintWidth,
      height - top,
    );
    ctx.restore();
  }

  /**
   * Rain ripple rings, riding the live surface where drops landed. Kept
   * deliberately quiet — faint, small, fading as they expand — so they read as
   * texture rather than as an event.
   */
  drawRings({ daylight, dt }: WaterFrame): void {
    if (this.rings.length === 0) {
      return;
    }

    const { ctx } = this;
    ctx.lineWidth = 0.6;

    for (let i = this.rings.length - 1; i >= 0; i -= 1) {
      const ring = this.rings[i];
      ring.life -= dt;

      if (ring.life <= 0) {
        this.rings.splice(i, 1);
        continue;
      }

      const radius = 2 + (RING_LIFE_S - ring.life) * 13;
      const y = this.surfaceYAt(ring.x);
      const alpha = 0.11 * (0.35 + 0.65 * daylight) * (ring.life / RING_LIFE_S);

      ctx.strokeStyle = `rgba(222,234,244,${alpha.toFixed(3)})`;
      ctx.beginPath();
      ctx.ellipse(ring.x, y, radius, radius * 0.26, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  /** Drop every transient so a location switch does not carry foam across. */
  reset(): void {
    this.rainDrops.length = 0;
    this.rings.length = 0;
  }
}
